import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { coursesService, Course } from '../services/coursesService';
import { creatorWorkflowService } from '../services/creatorWorkflowService';
import { useAuth } from '../contexts/AuthContext';
import AccessDenied from '../components/AccessDenied';
import type { CourseDraft } from '../types/creator-workflow';
import { getSmallThumbnailUrl } from '../utils/mediaUrl';
import './CreatorCoursesPage.css';
import { BilingualTitle } from '../components/common/BilingualTitle';

type Tab = 'drafts' | 'published';

const CreatorCoursesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTranslation(['course', 'common']);

  const activeTab = (searchParams.get('tab') as Tab) || 'drafts';

  // Shared state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAccessDenied, setIsAccessDenied] = useState(false);

  // Drafts state
  const [drafts, setDrafts] = useState<CourseDraft[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Published state
  const [publishedCourses, setPublishedCourses] = useState<Course[]>([]);
  const [publishedCount, setPublishedCount] = useState<number>(0);
  const [publishedTotal, setPublishedTotal] = useState<number>(0);
  const [loadingMorePublished, setLoadingMorePublished] = useState(false);
  const [editStartingCourseId, setEditStartingCourseId] = useState<string | null>(null);

  const setTab = (tab: Tab) => {
    setSearchParams({ tab });
  };

  // Load drafts
  const loadDrafts = async () => {
    setLoading(true);
    setError(null);
    setIsAccessDenied(false);
    try {
      const response = await creatorWorkflowService.getCreatorDrafts();
      const allDrafts = response.data || [];
      const unpublishedDrafts = allDrafts.filter(
        (draft: CourseDraft) => !draft.is_published && !draft.published_course_id
      );
      setDrafts(unpublishedDrafts);
      setPublishedCount(
        (response as any).publishedCount ??
        allDrafts.filter((d: CourseDraft) => d.is_published || d.published_course_id).length
      );
    } catch (err: any) {
      console.error('Failed to load drafts:', err);
      if (err.response?.status === 403 || err.message?.includes('403')) {
        setIsAccessDenied(true);
      } else {
        setError(err.message || t('common:failedToLoad'));
      }
    } finally {
      setLoading(false);
    }
  };

  const PUBLISHED_PAGE_SIZE = 20;

  // Load published courses (first page)
  const loadPublished = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    setIsAccessDenied(false);
    try {
      const { courses, pagination } = await coursesService.getCourses({
        creatorId: user.id,
        limit: PUBLISHED_PAGE_SIZE,
        offset: 0,
      });
      setPublishedCourses(courses);
      setPublishedTotal(pagination.total);
      setPublishedCount(pagination.total);
    } catch (err: any) {
      console.error('Failed to load courses:', err);
      if (err.response?.status === 403 || err.message?.includes('403')) {
        setIsAccessDenied(true);
      } else {
        setError(err.message || t('course:published.failedToLoad'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Load more published courses (infinite scroll)
  const loadingMoreRef = useRef(false);
  const publishedCoursesRef = useRef(publishedCourses);
  publishedCoursesRef.current = publishedCourses;

  const loadMorePublished = useCallback(async () => {
    if (loadingMoreRef.current || !user?.id) return;
    loadingMoreRef.current = true;
    setLoadingMorePublished(true);
    try {
      const { courses: data, pagination } = await coursesService.getCourses({
        creatorId: user.id,
        limit: PUBLISHED_PAGE_SIZE,
        offset: publishedCoursesRef.current.length,
      });
      setPublishedCourses(prev => [...prev, ...data]);
      setPublishedTotal(pagination.total);
    } catch (err: any) {
      console.error('Failed to load more courses:', err);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMorePublished(false);
    }
  }, [user?.id]);

  const hasMorePublished = publishedCourses.length < publishedTotal;

  // Infinite scroll observer (callback ref)
  const publishedObserverRef = useRef<IntersectionObserver | null>(null);

  // Cleanup observer on unmount to prevent memory leak
  useEffect(() => {
    return () => { publishedObserverRef.current?.disconnect(); };
  }, []);

  const publishedSentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (publishedObserverRef.current) {
      publishedObserverRef.current.disconnect();
      publishedObserverRef.current = null;
    }
    if (!node) return;
    publishedObserverRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMorePublished();
        }
      },
      { rootMargin: '200px' },
    );
    publishedObserverRef.current.observe(node);
  }, [loadMorePublished]);

  useEffect(() => {
    if (activeTab === 'drafts') {
      loadDrafts();
    } else {
      loadPublished();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user?.id]);

  // Draft actions
  const handleCreateNew = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const newDraft = await creatorWorkflowService.createDraft({
        cefr_level: 'A2',
        content_type: 'dialogue',
      });
      navigate(`/creator/wizard/${newDraft.id}`);
    } catch (err: any) {
      console.error('Failed to create draft:', err);
      alert(t('course:drafts.createNewFailed', { error: err.message || 'Unknown error' }));
    } finally {
      setIsCreating(false);
    }
  };

  // Open a published course for editing — backend clones it into a shadow
  // draft, then we redirect into the wizard's subtitle-editing stage. The
  // existing wizard handles save/cancel/republish via edit-mode CTAs.
  const handleEditPublishedCourse = async (courseId: string) => {
    if (editStartingCourseId) return;
    const confirmed = window.confirm(t('course:published.editConfirm'));
    if (!confirmed) return;
    setEditStartingCourseId(courseId);
    try {
      const { draftId } = await creatorWorkflowService.startEditPublishedCourse(courseId);
      // Land on the wizard root — it routes to the draft's current_stage,
      // which startEdit set to CONVERSATIONAL_EDITING. The creator can pivot
      // to the subtitle editor from there if they need timing edits.
      navigate(`/creator/wizard/${draftId}`);
    } catch (err: any) {
      console.error('Failed to start edit:', err);
      alert(t('course:published.editStartFailed', { error: err.message || 'Unknown error' }));
    } finally {
      setEditStartingCourseId(null);
    }
  };

  const handleDeleteDraft = async (draftId: string) => {
    setIsDeleting(true);
    try {
      await creatorWorkflowService.deleteDraft(draftId);
      setDrafts(drafts.filter(d => d.id !== draftId));
      setDeleteConfirmId(null);
    } catch (err: any) {
      console.error('Failed to delete draft:', err);
      alert(t('course:drafts.deleteFailed', { error: err.message || 'Unknown error' }));
    } finally {
      setIsDeleting(false);
    }
  };

  // Helpers
  const getStageLabel = (stage: string): string => {
    const stageMap: Record<string, string> = {
      'material_input': t('course:drafts.stageLabels.materialInput'),
      'subtitle_editing': t('course:drafts.stageLabels.subtitleEditing'),
      'generation': t('course:drafts.stageLabels.aiGeneration'),
      'parsing': t('course:drafts.stageLabels.nlpParsing'),
      'audio_generation': t('course:drafts.stageLabels.audioGeneration'),
      'audio_merging': t('course:drafts.stageLabels.audioMerging'),
      'validation': t('course:drafts.stageLabels.validation'),
      'publishing': t('course:drafts.stageLabels.publishing'),
      'published': t('course:drafts.stageLabels.published'),
    };
    return stageMap[stage] || stage;
  };

  const getStageProgress = (stage: string): number => {
    const stageOrder = [
      'material_input', 'subtitle_editing', 'generation', 'parsing',
      'audio_generation', 'audio_merging', 'validation', 'publishing', 'published',
    ];
    const index = stageOrder.indexOf(stage);
    if (index === -1) return 0;
    return Math.round((index / (stageOrder.length - 1)) * 100);
  };

  const getStageColor = (stage: string): string => {
    if (stage === 'published') return '#10b981';
    if (stage === 'validation' || stage === 'publishing') return '#8b5cf6';
    if (stage === 'audio_generation' || stage === 'audio_merging') return '#f59e0b';
    if (stage === 'parsing') return '#3b82f6';
    return '#6b7280';
  };

  const formatRelativeDate = (dateString: string | Date): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return t('common:today');
    if (days === 1) return t('common:yesterday');
    if (days < 7) return t('course:drafts.daysAgo', { count: days });
    return date.toLocaleDateString('zh-TW', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('zh-TW', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  // --- Render ---

  if (isAccessDenied) {
    return (
      <div className="creator-courses-page">
        <AccessDenied />
      </div>
    );
  }

  return (
    <div className="creator-courses-page">
      {/* Header */}
      <div className="creator-courses-header">
        <div className="header-tabs">
          <button
            className={`tab-btn ${activeTab === 'drafts' ? 'active' : ''}`}
            onClick={() => setTab('drafts')}
          >
            {t('course:drafts.title')}
            {drafts.length > 0 && <span className="tab-count">{drafts.length}</span>}
          </button>
          <button
            className={`tab-btn ${activeTab === 'published' ? 'active' : ''}`}
            onClick={() => setTab('published')}
          >
            {t('course:published.title')}
            {publishedCount > 0 && <span className="tab-count">{publishedCount}</span>}
          </button>
        </div>
        {activeTab === 'drafts' && (
          <button
            className="create-new-btn"
            onClick={handleCreateNew}
            disabled={isCreating}
          >
            {isCreating ? t('common:creating') : t('course:drafts.createNew')}
          </button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="creator-courses-loading">
          <div className="loading-spinner"></div>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="creator-courses-error">
          <p>{error}</p>
          <button onClick={activeTab === 'drafts' ? loadDrafts : loadPublished}>
            {t('common:retry')}
          </button>
        </div>
      )}

      {/* Drafts Tab */}
      {!loading && !error && activeTab === 'drafts' && (
        drafts.length === 0 ? (
          <div className="empty-inline">
            <span className="empty-text">{t('course:drafts.noDrafts')}</span>
          </div>
        ) : (
          <div className="drafts-grid">
            {drafts.map((draft) => (
              <div key={draft.id} className="draft-card">
                <div className="draft-card-header">
                  <div className="draft-status">
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getStageColor(draft.current_stage) }}
                    >
                      {getStageLabel(draft.current_stage)}
                    </span>
                  </div>
                  <button
                    className="delete-btn"
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(draft.id); }}
                    title={t('course:drafts.deleteDraft')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                    </svg>
                  </button>
                </div>

                <div className="draft-card-body" onClick={() => navigate(`/creator/wizard/${draft.id}`)}>
                  <BilingualTitle
                    title={draft.title || ''}
                    titles={draft.titles}
                    fallback={t('course:drafts.untitledDraft')}
                    variant="card"
                    as="h3"
                    className="draft-title"
                  />

                  <div className="draft-meta">
                    {draft.cefr_level && <span className="meta-item level">{draft.cefr_level}</span>}
                    {draft.content_type && <span className="meta-item type">{draft.content_type}</span>}
                    {draft.material_source && <span className="meta-item source">{draft.material_source}</span>}
                  </div>

                  <div className="draft-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${getStageProgress(draft.current_stage)}%`,
                          backgroundColor: getStageColor(draft.current_stage),
                        }}
                      />
                    </div>
                    <span className="progress-text">
                      {t('course:drafts.percentComplete', { percent: getStageProgress(draft.current_stage) })}
                    </span>
                  </div>

                  <div className="draft-footer">
                    <span className="draft-date">
                      {t('course:drafts.updated', { date: formatRelativeDate(draft.updated_at) })}
                    </span>
                    <Link
                      to={`/creator/wizard/${draft.id}`}
                      className="continue-btn"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('course:drafts.continueEditing')}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Published Tab */}
      {!loading && !error && activeTab === 'published' && (
        publishedCourses.length === 0 ? (
          <div className="empty-inline">
            <span className="empty-text">{t('course:published.noCourses')}</span>
          </div>
        ) : (
          <>
            <div className="courses-grid">
              {publishedCourses.map((course) => (
                <div
                  key={course.id}
                  className="course-card"
                  onClick={() => navigate(`/creator/courses/${course.id}`)}
                >
                  <div className="course-card-thumbnail">
                    {course.thumbnailUrl || course.coverImageUrl ? (
                      <img src={getSmallThumbnailUrl(course.thumbnailUrl || course.coverImageUrl)} alt={course.title} />
                    ) : (
                      <div className="thumbnail-placeholder">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M4 16l4.586-4.586a2 2 0 0 1 2.828 0L16 16m-2-2l1.586-1.586a2 2 0 0 1 2.828 0L20 14m-6-6h.01M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </div>

                  <div className="course-card-body">
                    <BilingualTitle
                      title={course.title}
                      titles={course.titles}
                      fallback={t('course:manage.untitledCourse')}
                      variant="card"
                      as="h3"
                      className="course-title"
                    />

                    <div className="course-meta">
                      {course.cefrLevel && <span className="meta-item level">{course.cefrLevel}</span>}
                      {course.wordCount && (
                        <span className="meta-item type">{t('common:words', { count: course.wordCount })}</span>
                      )}
                      {course.accent && (
                        <span className="meta-item accent">
                          {String(t(`course:filters.accents.${course.accent}`, { defaultValue: course.accent }))}
                        </span>
                      )}
                    </div>

                    {course.description && (
                      <p className="course-description">{course.description}</p>
                    )}

                    <div className="course-footer">
                      <span className="course-date">
                        {t('course:published.publishedDate', { date: formatDate(course.publishedAt || course.createdAt) })}
                      </span>
                      <div className="course-footer-actions">
                        <button
                          type="button"
                          className="edit-published-btn"
                          onClick={(e) => { e.stopPropagation(); handleEditPublishedCourse(course.id); }}
                          disabled={editStartingCourseId === course.id}
                          title={t('course:published.editTooltip')}
                        >
                          {editStartingCourseId === course.id
                            ? t('common:loading')
                            : t('course:published.editCourse')}
                        </button>
                        <Link
                          to={`/creator/courses/${course.id}`}
                          className="view-btn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t('course:published.viewCourse')}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {hasMorePublished && (
              <div className="load-more-container" ref={publishedSentinelRef}>
                {loadingMorePublished && <span className="load-more-spinner" />}
                <span className="load-more-count">
                  {publishedCourses.length} / {publishedTotal}
                </span>
              </div>
            )}
          </>
        )
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="drafts-delete-overlay" onClick={() => !isDeleting && setDeleteConfirmId(null)}>
          <div className="drafts-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('course:drafts.deleteTitle')}</h3>
            <p>{t('course:drafts.deleteConfirmMessage', { title: drafts.find(d => d.id === deleteConfirmId)?.title || t('course:drafts.untitledDraft') })}</p>
            <p className="warning">{t('course:drafts.deleteWarning')}</p>
            <div className="confirm-buttons">
              <button className="cancel-btn" onClick={() => setDeleteConfirmId(null)} disabled={isDeleting}>
                {t('common:cancel')}
              </button>
              <button className="confirm-delete-btn" onClick={() => handleDeleteDraft(deleteConfirmId)} disabled={isDeleting}>
                {isDeleting ? t('common:deleting') : t('common:delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorCoursesPage;
